// Media — Office insight. Every photo and file a crew has attached to a job,
// in one place, with what the library costs and a way to delete one file.
//
//   GET    /api/media/all?file_type=&sort=&job_id=   the library
//   GET    /api/media/stats                          the totals strip
//   DELETE /api/media/:mediaId                       one file, permanently
//   GET    /api/jobs/all                             the job filter, and the
//                                                    client name a media row
//                                                    does not carry
//
// ── The contract, read off server/routes/media.js ───────────────────────────
//
// `/all` takes THREE query parameters and nothing else — no paging, no date
// window, no free-text search:
//
//   job_id     exact match on m.job_id
//   file_type  exact match on m.file_type, and the literal 'all' is treated as
//              no filter at all
//   sort       'oldest' | 'name' | 'job', anything else (including the
//              default 'newest') means created_at DESC
//
// It answers `{ total, media: [...], grouped: [...] }`. A row is `m.*` from
// job_media plus `j.title AS job_title` and `u.name AS uploaded_by_name`:
//
//   id · job_id · user_id · filename · original_name · file_path · file_type
//   · file_size · caption · created_at · job_title · uploaded_by_name
//
// `file_path` is already the URL — `/uploads/job-media/<filename>`, written by
// the upload handler and served from the app's own origin, which is what
// apps/shared/job/JobPanels.js renders too. Nothing here builds a path.
//
// `/stats` answers ONE row with these five names, and no others:
//
//   total_files · total_size · image_count · pdf_count · document_count
//
// SUM() over no rows is NULL in SQLite, so an empty library answers
// total_files 0 with total_size NULL. That is the one place a zero is the
// truth, and statTiles() below is written around it.
//
// WHO MAY DELETE: the uploader, the job's lead worker (jobs.user_id), or any
// admin/manager. Which means every row this screen can show is a row the
// viewer may delete — `/all` filters to `j.user_id = me` for anybody who is
// not an admin or manager, so a non-manager only ever sees rows they lead.
// There is therefore no role test in this file: the button is offered, and if
// the server ever refuses one, the refusal is rendered as the sentence the
// server sent. Duplicating the rule here is how a screen ends up hiding an
// action the server would have allowed.
//
// The `grouped` half of the answer is deliberately unused. The classic screen
// kept a flat list and a grouped list and filtered them with different
// predicates, so a search could empty one and not the other; one list cannot
// disagree with itself.

import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { del, describeError } from '../../shared/http';
import { Icon } from '../../../ui';
import { Stat, FwButton, FwField, FwSelect, Skeleton, EmptyState, Notice } from '../../../ui/fw';
import {
  useOfficeQuery, useAction, PageHeader, Toolbar, SearchBox, Panel, LoadError, FormError,
  ConfirmSheet, pickerNote, matches, fmtDateTime,
} from '../records/OfficePage';
import { fmtBytes, fileLabel, isImageFile } from '../../shared/job/mediaKinds';
import './Media.css';

const DASH = '—';

/** The file_type values the server actually stores, plus the 'all' sentinel. */
export const TYPES = [
  { value: 'all', label: 'All types' },
  { value: 'image', label: 'Photos' },
  { value: 'pdf', label: 'PDFs' },
  { value: 'document', label: 'Other files' },
];

/** The sort keys `/all` branches on. Offering one it ignores does nothing. */
export const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'name', label: 'By file name' },
  { value: 'job', label: 'By job' },
];

/** The fields the search box looks in. */
export const SEARCH_FIELDS = ['original_name', 'job_title', 'caption', 'uploaded_by_name'];

/**
 * The request path for a set of filters. Exported so the parameter NAMES are
 * checked against the route file rather than trusted (OfficeMedia.test.js).
 * 'all' and 'newest' are the server's own defaults, so they are left off.
 */
/** How many files one page asks for. The handler clamps anything above 500. */
export const MEDIA_PAGE = 200;

export function mediaPath({ type = 'all', sort = 'newest', jobId = '', limit = 0 } = {}) {
  const qs = new URLSearchParams();
  if (type && type !== 'all') qs.set('file_type', type);
  if (sort && sort !== 'newest') qs.set('sort', sort);
  if (jobId) qs.set('job_id', String(jobId));
  // Asked for explicitly. GET /media/all has no default cap — deliberately,
  // since a cap a caller did not ask for would silently show the first N of a
  // larger library — so a screen that wants a page has to say so, and gets
  // `total` telling it what it is a page OF.
  if (limit) qs.set('limit', String(limit));
  const s = qs.toString();
  return s ? `/media/all?${s}` : '/media/all';
}

/** A byte count for a person. MISSING IS NOT ZERO; a real 0 still reads '0 B'. */
// The byte formatter moved to apps/shared/job/mediaKinds.js on 2026-09-09 —
// the job panels and the phone print the same string now, and one definition
// is the rule (CLAUDE.md). Re-exported, so this file's call sites and
// OfficeMedia.test.js are unchanged.
export { fmtBytes };

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/**
 * The totals strip, from the /stats payload — or NO STRIP AT ALL.
 *
 * Four numbers over a photo grid read as arithmetic, and arithmetic is
 * trusted (docs/AGENT_LEARNINGS.md, 2026-08-17). So this returns an empty
 * array for anything it cannot vouch for: no answer, or an answer with no
 * numeric total_files in it. The screen then says the totals could not be
 * loaded, which is a claim a person can see is a claim.
 *
 * The one exception is the empty library, and it is the server's own SQL that
 * licenses it: `COUNT(*)` is 0 while every `SUM(...)` over no rows is NULL, so
 * when total_files is 0 the missing counts and the missing size ARE zero, and
 * printing them as zero is the honest reading. Without that, a genuinely empty
 * library would show three dashes and read as a failure.
 */
export function statTiles(stats) {
  if (!stats) return [];
  const files = num(stats.total_files);
  if (files === null || !Number.isFinite(files)) return [];
  const empty = files === 0;
  const images = empty ? 0 : num(stats.image_count);
  const pdfs = empty ? 0 : num(stats.pdf_count);
  const others = empty ? 0 : num(stats.document_count);
  const documents = pdfs === null || others === null ? null : pdfs + others;
  return [
    { label: 'Files', value: files, hint: 'in the library' },
    { label: 'Photos', value: images === null ? DASH : images },
    { label: 'Documents', value: documents === null ? DASH : documents, hint: 'PDFs and other files' },
    { label: 'Storage', value: fmtBytes(empty ? 0 : stats.total_size) },
  ];
}

/**
 * What an empty grid MEANS — which depends on whether the library has been
 * fully loaded.
 *
 * The search box filters the rows that arrived, and paging means not all of
 * them have. So "Nothing matches these filters" over a part-loaded library is
 * a claim about files this screen has never seen. That is the same defect the
 * rest of this file is written around, one step further in: not a refusal
 * rendered as data, but a SUBSET rendered as the whole.
 *
 * `total === null` means the answer carried no total, so nothing can be said
 * about what is unloaded and the wording stays neutral.
 */
export function emptyNote({ filtering, more, total } = {}) {
  if (more) {
    return {
      icon: 'search',
      title: 'Nothing in the files loaded so far matches',
      body: 'There are more files to load — use Show more below, or narrow by type or job so the server does the filtering.',
    };
  }
  if (filtering) {
    return {
      icon: 'search',
      title: 'Nothing matches these filters',
      body: 'Clear the search or choose another type or job.',
    };
  }
  if (total === null || total === undefined) {
    return {
      icon: 'camera',
      title: 'No files to show',
      body: 'Anything a crew photographs or attaches to a job shows up here.',
    };
  }
  return {
    icon: 'camera',
    title: 'No photos or files yet',
    body: 'Anything a crew photographs or attaches to a job shows up here.',
  };
}

/** job_id → { title, client_name, no_client } from /jobs/all, or null if it never answered. */
export function jobIndex(jobs) {
  if (!Array.isArray(jobs)) return null;
  const byId = new Map();
  jobs.forEach((j) => byId.set(String(j.id), j));
  return byId;
}

function MediaCard({ item, job, onDelete }) {
  const name = fileLabel(item);
  // The shared reading (apps/shared/job/mediaKinds.js), not `file_type ===
  // 'image'`. Rows written before the rebuild carry a raw MIME type in that
  // column — the fixture in rebuildRoutes.test.js has 'image/jpeg' — and a
  // bare equality draws those as a document glyph on a photo. One definition,
  // so this screen, the job panel and the phone agree about one row.
  const isImage = isImageFile(item);
  // `job` is undefined when the job list never answered. An absent client name
  // then renders as nothing — "No client" would be a statement about the job
  // made from a request that failed. `no_client` is the server's own flag
  // (server/routes/jobs.js flagNoClient), so it is only read when it arrived.
  const client = job ? (job.client_name || (job.no_client ? 'No client on the job' : '')) : '';
  return (
    <li className="or-media__card">
      <a
        className="or-media__thumb"
        href={item.file_path}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${name}`}
      >
        {isImage ? (
          <img src={item.file_path} alt={item.caption || name} loading="lazy" />
        ) : (
          <span className="or-media__glyph" aria-hidden="true"><Icon name="document" /></span>
        )}
      </a>
      <div className="or-media__body">
        <div className="or-media__name" title={name}>{name}</div>
        <div className="or-media__job">{item.job_title || `Job #${item.job_id}`}</div>
        {client ? <div className="or-media__client">{client}</div> : null}
        {item.caption ? <div className="or-media__caption">{item.caption}</div> : null}
        <div className="or-media__meta">
          {[fmtBytes(item.file_size), fmtDateTime(item.created_at), item.uploaded_by_name].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className="or-media__actions">
        <a className="or-media__link" href={item.file_path} download={name}>
          <Icon name="download" /> <span>Download</span>
        </a>
        <FwButton variant="quiet" icon="trash" aria-label={`Delete ${name}`} onClick={() => onDelete(item)}>
          Delete
        </FwButton>
      </div>
    </li>
  );
}

export default function Media() {
  const qc = useQueryClient();
  const [type, setType] = useState('all');
  const [sort, setSort] = useState('newest');
  const [jobId, setJobId] = useState('');
  const [q, setQ] = useState('');
  const [pending, setPending] = useState(null);
  const [limit, setLimit] = useState(MEDIA_PAGE);
  const { busy, error: actionError, run, clear } = useAction();

  const listPath = mediaPath({ type, sort, jobId, limit });
  const listQ = useOfficeQuery(['media', 'all', listPath], listPath);
  const statsQ = useOfficeQuery(['media', 'stats'], '/media/stats');
  // /jobs/all is admin/manager only (server/routes/jobs.js:98), so a bookkeeper
  // gets a 403 here on every load. It supplies the job picker AND the client
  // name; both have to survive its absence without inventing anything.
  const jobsQ = useOfficeQuery(['jobs', 'all'], '/jobs/all');

  // A FAILED list renders no rows, even when an earlier filter had loaded some.
  // react-query keeps the last successful data for a key it refetches, so
  // without this a refused reload leaves the previous filter's files on screen
  // under a control naming a different one — with Delete beside each of them.
  const rows = useMemo(() => {
    if (listQ.error || !listQ.data) return [];
    const all = Array.isArray(listQ.data.media) ? listQ.data.media : [];
    return all.filter((m) => matches(m, SEARCH_FIELDS, q));
  }, [listQ.error, listQ.data, q]);

  // Same rule for the totals: a refused refresh (the delete below invalidates
  // them) must drop the numbers rather than leave the pre-delete ones standing
  // over a grid that just lost a file.
  const tiles = statsQ.error ? [] : statTiles(statsQ.data);

  const jobs = useMemo(() => jobIndex(jobsQ.data), [jobsQ.data]);
  const jobOptions = useMemo(() => (Array.isArray(jobsQ.data) ? jobsQ.data : []), [jobsQ.data]);

  const confirmDelete = async () => {
    const item = pending;
    if (!item) return;
    const ok = await run(async () => {
      await del(`/media/${item.id}`);
      await qc.invalidateQueries({ queryKey: ['media'] });
    }, 'Could not delete the file.');
    if (ok) setPending(null);
  };

  // A filter change starts again at one page. Carrying a raised limit across
  // filters would ask for 600 rows of a two-row result; carrying an OFFSET
  // across would be the page-3-of-a-two-row-result bug the activity log had.
  const refilter = (set) => (value) => { set(value); setLimit(MEDIA_PAGE); };

  const filtering = !!q || type !== 'all' || !!jobId;
  const total = listQ.data && Number.isFinite(Number(listQ.data.total)) ? Number(listQ.data.total) : null;
  // What was LOADED is not what MATCHED and not what the search box left on
  // screen. Three numbers, and collapsing any two of them is how a screen ends
  // up claiming a library is smaller than it is.
  const loaded = listQ.data && Number.isFinite(Number(listQ.data.returned))
    ? Number(listQ.data.returned)
    : (listQ.data && Array.isArray(listQ.data.media) ? listQ.data.media.length : null);
  const more = total !== null && loaded !== null && loaded < total;
  const meta = listQ.error || total === null
    ? 'Photos and files attached to jobs'
    : q
      ? `${rows.length} matching · ${loaded} of ${total} loaded`
      : `${loaded} of ${total} file${total === 1 ? '' : 's'}`;

  return (
    <div className="or-page" data-screen="office-Media">
      <PageHeader title="Media" meta={meta} />

      {statsQ.isPending ? <Skeleton lines={2} /> : null}
      {statsQ.error ? (
        <Notice tone="warn" className="or-error">
          {`Library totals could not be loaded, so none are shown — this is not a count of zero. ${describeError(statsQ.error, 'Reload to try again.')}`}
        </Notice>
      ) : null}
      {tiles.length > 0 ? (
        <div className="or-stats">
          {tiles.map((t) => <Stat key={t.label} label={t.label} value={t.value} hint={t.hint} />)}
        </div>
      ) : null}

      <Toolbar>
        <SearchBox value={q} onChange={setQ} placeholder="Search file, job, caption" label="Search media" />
        <FwField label="Type" htmlFor="or-media-type">
          <FwSelect value={type} onChange={(e) => refilter(setType)(e.target.value)}>
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </FwSelect>
        </FwField>
        <FwField label="Sort" htmlFor="or-media-sort">
          <FwSelect value={sort} onChange={(e) => refilter(setSort)(e.target.value)}>
            {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </FwSelect>
        </FwField>
        {/*
          An empty picker looks like data, not like a failure — the whole
          reason pickerNote exists (OfficePage.js). Disabled only when the list
          really arrived and was empty.
        */}
        <FwField label="Job" htmlFor="or-media-job" error={pickerNote(jobsQ.error, 'job')}>
          <FwSelect
            value={jobId}
            onChange={(e) => refilter(setJobId)(e.target.value)}
            disabled={!jobsQ.error && jobOptions.length === 0}
          >
            <option value="">All jobs</option>
            {jobOptions.map((j) => <option key={j.id} value={String(j.id)}>{j.title}</option>)}
          </FwSelect>
        </FwField>
      </Toolbar>

      <Panel title="Library">
        {listQ.isPending ? <Skeleton lines={4} /> : null}
        <LoadError error={listQ.error} fallback="Could not load the media library." />
        {listQ.error ? (
          // "No media yet" is a claim about the library, and after a refused
          // request nobody knows whether it holds.
          <EmptyState icon="camera" title="The library could not be loaded">
            This is not an empty library — see the message above, then reload.
          </EmptyState>
        ) : null}
        {!listQ.error && listQ.data && rows.length === 0 ? (
          (() => {
            const e = emptyNote({ filtering, more, total });
            return <EmptyState icon={e.icon} title={e.title}>{e.body}</EmptyState>;
          })()
        ) : null}
        {rows.length > 0 ? (
          <ul className="or-media__grid">
            {rows.map((m) => (
              <MediaCard key={m.id} item={m} job={jobs ? jobs.get(String(m.job_id)) : undefined} onDelete={setPending} />
            ))}
          </ul>
        ) : null}
        {/*
          A SIBLING of the grid, not a child of it. The search box filters the
          rows that are LOADED, so a search can empty the screen while the
          library still has pages left — and a Show more hidden inside the grid
          would be gone at exactly the moment it is needed.
        */}
        {more ? (
          <div className="or-actions or-media__more">
            <FwButton variant="ghost" onClick={() => setLimit((n) => n + MEDIA_PAGE)} loading={listQ.isFetching}>
              {`Show more (${total - loaded} left)`}
            </FwButton>
          </div>
        ) : null}
      </Panel>

      <ConfirmSheet
        open={!!pending}
        title="Delete this file?"
        confirmLabel="Delete file"
        busy={busy}
        onConfirm={confirmDelete}
        onClose={() => { setPending(null); clear(); }}
      >
        {/*
          One file, named. Nothing here counts anything: the sheet says exactly
          which file goes, and where it was.
        */}
        <FormError error={actionError} />
        <p>
          <strong>{pending ? (pending.original_name || pending.filename) : ''}</strong>
          {pending && pending.job_title ? ` — attached to ${pending.job_title}` : ''}
        </p>
        <p>The file itself is deleted from the server as well as from the job. This cannot be undone.</p>
      </ConfirmSheet>
    </div>
  );
}
